import { z } from "zod";
import { reseauSchema } from "./reseaux";

/**
 * Le contrat des chiffres des reseaux (lot M4) : ce que la lecture rend a la regie et au MCP de La Taverne.
 * Que des nombres, des identifiants, des liens publics et des dates : aucun texte de post, aucun pseudo.
 */

/** Les codes de cause d'un releve, liste fermee : un lecteur n'en emploie pas d'autres. */
export const CODES_CHIFFRES = [
  "youtube_non_autorise",
  "youtube_a_reautoriser",
  "youtube_quota",
  "youtube_refus",
  "youtube_injoignable",
  "bluesky_non_branche",
  "bluesky_refus",
  "bluesky_injoignable",
  "tiktok_non_autorise",
  "tiktok_a_reautoriser",
  "tiktok_refus",
  "tiktok_injoignable",
  "facebook_non_autorise",
  "facebook_a_reautoriser",
  "facebook_statistiques_refusees",
  "facebook_liste_refusee",
  "facebook_publications_illisibles",
  "facebook_refus",
  "facebook_injoignable",
  "instagram_non_autorise",
  "instagram_a_reautoriser",
  "instagram_statistiques_refusees",
  "instagram_refus",
  "instagram_injoignable",
  "threads_non_autorise",
  "threads_a_reautoriser",
  "threads_statistiques_refusees",
  "threads_refus",
  "threads_injoignable",
  "releve_interrompu",
  "releve_base",
] as const;
export type CodeChiffres = (typeof CODES_CHIFFRES)[number];

const nombreSchema = z.number().int().nonnegative().nullable();

export const metriquesSchema = z.object({
  vues: nombreSchema,
  portee: nombreSchema,
  jaime: nombreSchema,
  commentaires: nombreSchema,
  partages: nombreSchema,
  enregistrements: nombreSchema,
  reposts: nombreSchema,
  citations: nombreSchema,
});
export type MetriquesContrat = z.infer<typeof metriquesSchema>;

export const sortePublicationSchema = z.enum(["stock", "video", "image", "texte"]);
export type SortePublication = z.infer<typeof sortePublicationSchema>;

export const origineSchema = z.enum(["castellan", "rattache", "hors_castellan"]);
export type OrigineChiffres = z.infer<typeof origineSchema>;

export const etatPasseSchema = z.enum([
  "ok",
  "partiel",
  "a_reautoriser",
  "non_branche",
  "echec",
  "jamais",
]);
export type EtatPasse = z.infer<typeof etatPasseSchema>;

export const periodeSchema = z.enum(["4s", "3m", "12m"]);
export type Periode = z.infer<typeof periodeSchema>;

export const ligneClassementSchema = z.object({
  cle: z.string(),
  publications: z.number().int().nonnegative(),
  vuesJ7Moyennes: z.number().int().nonnegative(),
});

export const chiffresSchema = z.object({
  depuis: z.string(), // AAAA-MM-JJ, Paris
  jusqua: z.string(),
  reseaux: z.array(
    z.object({
      reseau: reseauSchema,
      etat: etatPasseSchema,
      cause: z.string().nullable(),
      releveLe: z.string().nullable(), // ISO
      abonnes: z.number().int().nonnegative().nullable(),
      abonnesExact: z.boolean(),
      evolution7j: z.number().int().nullable(),
    }),
  ),
  abonnes: z.array(
    z.object({
      reseau: reseauSchema,
      points: z.array(z.object({ jour: z.string(), nombre: z.number().int().nonnegative() })),
    }),
  ),
  vuesParSemaine: z.array(
    z.object({
      reseau: reseauSchema,
      points: z.array(
        z.object({
          lundi: z.string(),
          vues: z.number().int().nonnegative(),
          publications: z.number().int().nonnegative(),
        }),
      ),
    }),
  ),
  publications: z.array(
    z.object({
      id: z.number().int(),
      reseau: reseauSchema,
      lien: z.string().nullable(),
      publieLe: z.string(),
      sorte: sortePublicationSchema,
      origine: origineSchema,
      jeu: z.string().nullable(),
      actuel: metriquesSchema,
      j1: metriquesSchema.nullable(),
      j7: metriquesSchema.nullable(),
      j30: metriquesSchema.nullable(),
    }),
  ),
  publicationsTronquees: z.boolean(),
  classements: z.object({
    parJeu: z.array(ligneClassementSchema),
    parSorte: z.array(ligneClassementSchema),
    parJour: z.array(ligneClassementSchema), // cle "1" (lundi) a "7"
    parHeure: z.array(ligneClassementSchema), // cle "0" a "23", Paris
  }),
});
export type Chiffres = z.infer<typeof chiffresSchema>;

export const releverReponseSchema = z.object({ lance: z.literal(true) });
